import { type PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';
import { signOut } from '../auth/signIn.js';
import type { ActiveTenantInfo } from '../auth/useActiveTenant.js';
import { isGroupAdmin } from '../lib/role.js';
import type { Membership } from '../api/types.js';
import { Sidebar } from './Sidebar.js';
import { TopBar } from './TopBar.js';
import { CommandPalette } from './CommandPalette.js';

interface AppShellProps extends PropsWithChildren {
  active: ActiveTenantInfo;
  // Legacy `crumb` prop retained for caller signature stability — TopBar
  // now derives the breadcrumb from `useLocation()` so this prop is no
  // longer consumed. Safe to drop in a future cleanup.
  crumb?: string;
  validatorOnly?: boolean;
  showAdminMembers?: boolean;
  callerMemberships?: Membership[];
}

// REQ-FE-034 area B — AppShell ported from the mockup source at
// `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/components/AppShell.tsx`.
// Composes the new `<TopBar>` + `<Sidebar>` with a `<main>` flex
// container. The mockup's vertical TopBar-first layout (`flex
// flex-col` with TopBar on top, Sidebar+main below) wins over the
// previous horizontal-only layout — fixes the bottom shortcut bar
// being trapped inside the list pane.
//
// Dropped from the mockup (live SPA doesn't have these yet — file
// follow-up REQs):
//   - WebhookDrawer + ⇧W binding
//   - SupplierSearchSheet (Annuaire is a route in live, not a sheet)
//
// Kept from FE-030 / FE-032 / FE-033:
//   - CommandPalette (⌘K)
//   - ⇧A → annuaire navigation (route, not sheet)
//   - TopBar avatar popover with sign-out (FE-032 D20)
//   - Mentions bell badge (FE-029c-2)
export function AppShell({
  active,
  children,
  validatorOnly = false,
  showAdminMembers = false,
  callerMemberships,
}: AppShellProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const { available, tenant } = active;
  const isAdmin = callerMemberships ? isGroupAdmin(callerMemberships) : showAdminMembers;
  const annuaireTenant = tenant ?? available[0] ?? null;
  const annuaireHref = annuaireTenant ? `/t/${annuaireTenant.tenantId}/annuaire` : null;

  // ⌘K / Ctrl+K → command palette · ⇧A → annuaire (route).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !inField) {
        e.preventDefault();
        setCmdkOpen(true);
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === 'a' && !inField && !e.metaKey && !e.ctrlKey) {
        if (annuaireHref) {
          e.preventDefault();
          navigate(annuaireHref);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [annuaireHref, navigate]);

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  const callerEmail = user?.email ?? null;
  const callerDisplayName = (user?.displayName ?? user?.email ?? '').trim();

  return (
    <div className="flex min-h-screen flex-col bg-cream-50 text-ink antialiased">
      <TopBar
        onOpenPalette={() => setCmdkOpen(true)}
        onSignOut={handleSignOut}
        callerEmail={callerEmail}
        callerDisplayName={callerDisplayName}
        tenants={available}
        annuaireHref={annuaireHref}
        validatorOnly={validatorOnly}
      />
      <div className="flex flex-1">
        <Sidebar
          tenants={available}
          activeTenantId={tenant?.tenantId ?? null}
          callerMemberships={callerMemberships ?? []}
          callerDisplayName={callerDisplayName}
          callerEmail={callerEmail}
          isAdmin={isAdmin}
          validatorOnly={validatorOnly}
          annuaireHref={annuaireHref}
        />
        {/* REQ-FE-036 area A: per-screen wrapper matches mockup
            pattern (`mx-auto max-w-6xl px-8 py-10` inside each route
            component, not at the shell level). Keeps split-pane
            invoice list free to use its own wider `max-w-7xl` outer. */}
        <main className="flex-1">{children}</main>
      </div>

      <CommandPalette open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
    </div>
  );
}
